import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, FileText, Sparkles, Loader2, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import NuevaOrden from "@/components/compras/NuevaOrden";
import OrdenDetalle from "@/components/compras/OrdenDetalle";
import RecepcionDetalle from "@/components/compras/RecepcionDetalle";
import OrdenesFiltros, { FILTROS_INI } from "@/components/compras/OrdenesFiltros";
import OrdenesTabla from "@/components/compras/OrdenesTabla";

export { fmt, ESTADO_OC } from "@/components/compras/ocUtils";

function useDebounce(v, ms = 350) {
  const [d, setD] = useState(v);
  useEffect(() => { const t = setTimeout(() => setD(v), ms); return () => clearTimeout(t); }, [v, ms]);
  return d;
}

export default function OrdenesTab({ proveedores, comps, ordenInicial, onCambio }) {
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [f, setF] = useState(FILTROS_INI);
  const fd = useDebounce(f);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(() => Number(localStorage.getItem("oc_size")) || 50);
  const [vista, setVista] = useState(() => localStorage.getItem("oc_vista") || "tabla");
  const [sel, setSel] = useState(null);
  const [rec, setRec] = useState(null);
  const [nueva, setNueva] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [importando, setImportando] = useState(false);
  const [cargando, setCargando] = useState(false);
  useEffect(() => { localStorage.setItem("oc_vista", vista); }, [vista]);
  useEffect(() => { localStorage.setItem("oc_size", String(size)); }, [size]);
  useEffect(() => { setPage(1); }, [fd]);

  async function importarDoc(e) {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setImportando(true);
    try {
      const form = new FormData(); form.append("file", file);
      const { data: d } = await api.post("/compras/ordenes/interpretar", form, { headers: { "Content-Type": "multipart/form-data" } });
      if (!d.items.length) return toast.error(`No se pudo relacionar ninguna partida con nuestros insumos (${d.sin_match.length} sin coincidencia)`);
      setPrefill({ proveedor_id: d.proveedor?.id || proveedores[0]?.id, items: d.items, nota: `Importado de ${d.documento?.tipo || "documento"} ${d.documento?.folio || ""}${d.documento?.emisor_nombre ? ` · ${d.documento.emisor_nombre}` : ""}`.trim(), sin_match: d.sin_match });
      setNueva(true); setSel(null); setRec(null);
      toast.success(`${d.items.length} partidas interpretadas${d.sin_match.length ? ` · ${d.sin_match.length} sin coincidencia (revísalas)` : ""}`);
    } catch (err) { toast.error(formatApiError(err)); } finally { setImportando(false); }
  }

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const params = Object.fromEntries(Object.entries({ ...fd, page, size }).filter(([, v]) => v !== "" && v != null));
      const { data: d } = await api.get("/compras/ordenes", { params }); setData(d);
    } catch (e) { toast.error(formatApiError(e)); } finally { setCargando(false); }
  }, [fd, page, size]);
  useEffect(() => { cargar(); }, [cargar]);

  const abrir = useCallback(async (id) => {
    try { const { data: d } = await api.get(`/compras/ordenes/${id}`); setSel(d); setRec(null); setNueva(false); } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { if (ordenInicial) abrir(ordenInicial); }, [ordenInicial, abrir]);

  const refrescar = async () => { await cargar(); onCambio?.(); if (sel) abrir(sel.id); };
  const abrirRecepcion = async (id) => {
    try { const { data: d } = await api.get(`/compras/recepciones/${id}`); setRec(d); } catch (e) { toast.error(formatApiError(e)); }
  };
  const enDetalle = nueva || rec || sel;
  const volver = () => { setSel(null); setRec(null); setNueva(false); };

  return (
    <div className="space-y-4">
      {!enDetalle && <>
        <div className="flex flex-wrap gap-2">
          <button data-testid="oc-nueva-button" onClick={() => { setPrefill(null); setNueva(true); setSel(null); setRec(null); }} className="btn-amber h-11 px-5 text-sm"><Plus className="w-4 h-4" /> Nueva orden de compra</button>
          <label data-testid="oc-importar-label" className="btn-ink h-11 px-5 text-sm cursor-pointer flex items-center justify-center gap-2">{importando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-brand-amber" />} Importar factura / PDF / Excel con IA<input data-testid="oc-importar-input" type="file" accept=".xml,.pdf,.xlsx,.xls" className="hidden" onChange={importarDoc} disabled={importando} /></label>
          {cargando && <span className="self-center text-xs text-brand-muted inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Cargando…</span>}
        </div>
        <OrdenesFiltros f={f} setF={setF} proveedores={proveedores} creadores={data.creadores} resumen={data.resumen} vista={vista} setVista={setVista} />
        <OrdenesTabla data={data} vista={vista} sel={sel} abrir={abrir} page={page} setPage={setPage} size={size} setSize={setSize} />
      </>}
      {enDetalle && <>
        <button data-testid="oc-volver" onClick={volver} className="btn-soft h-9 px-3 text-sm inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Volver al histórico de órdenes</button>
        {nueva && <NuevaOrden proveedores={proveedores} comps={comps} orden={prefill} onCreada={(o) => { setNueva(false); refrescar(); abrir(o.id); }} onCancelar={() => setNueva(false)} />}
        {!nueva && rec && <RecepcionDetalle rec={rec} comps={comps} orden={sel} onVolver={() => setRec(null)} onCambio={(r) => { setRec(r); }} onConfirmada={() => { setRec(null); refrescar(); }} />}
        {!nueva && !rec && sel && <OrdenDetalle orden={sel} proveedores={proveedores} comps={comps} onCambio={refrescar} onAbrirRecepcion={abrirRecepcion} onNuevaRecepcion={(r) => { setRec(r); refrescar(); }} />}
      </>}
      {!enDetalle && !data.total && !cargando && <div className="hidden" data-testid="oc-vacio"><FileText className="w-8 h-8" /></div>}
    </div>
  );
}
